import {
  findAddonById,
  getOptimalPrice,
} from '@bbkPricingPage/helpers/addon-helper';
import {
  getEffectiveAmount,
  priceMultiple,
} from '@bbkPricingPage/helpers/amount-calculator';
import {
  type Addon,
  type AddonType,
  type Frequency,
  type Item,
  type PeriodUnit,
  type Price,
} from '@bbkPricingPage/types/pricing-table-types';

type SelectedAddon = { addonId: string; quantity?: number };

export const processAddons = (
  selectedAddons: Map<string, SelectedAddon>,
  currency: string,
  frequency: Frequency,
  selectedPlan: Item,
  planPrice: Price,
  allAddons: Addon[],
  variantId?: string
): {
  processedAddons: {
    addon: Addon;
    addonPrice: Price;
    quantity: number | undefined;
    multiplier: number;
    amount: number;
    addonType: AddonType;
  }[];
  total: number;
} => {
  let total = 0;

  const processedAddons = Array.from(selectedAddons.values())
    .map(({ addonId, quantity }) => {
      const addon = findAddonById(allAddons, addonId);
      if (!addon) return;
      const addonPrice = getOptimalPrice({
        prices: addon.prices,
        currency,
        itemId: selectedPlan.billing_system_id,
        itemType: addon.item_type,
        frequency: frequency,
        variantId: variantId,
      });
      if (!addonPrice) return null;

      const addonValue = getEffectiveAmount(addonPrice, quantity, frequency);
      if (!addonValue) return null;

      const multiplier = priceMultiple(
        planPrice.period as number,
        planPrice.period_unit as PeriodUnit,
        addonPrice.period,
        addonPrice.period_unit
      );
      const amount = addonValue * multiplier;
      total += amount;
      const addonType = selectedPlan.attached_addons?.find(
        (aa) => aa.billing_system_id === addon.billing_system_id
      )?.type as AddonType;
      return {
        addon: addon,
        addonPrice: addonPrice,
        quantity: quantity,
        multiplier: multiplier,
        amount: amount,
        addonType: addonType,
      };
    })
    .filter(
      (item): item is NonNullable<typeof item> =>
        item !== null && item !== undefined
    )
    .sort((a, b) =>
      (a.addon.customization?.name || '').localeCompare(
        b.addon.customization?.name || ''
      )
    );

  return { processedAddons, total };
};
